import { action, observable, runInAction } from 'mobx'
import moment from 'moment-timezone'
import ListStore from '../../../shared-lib/stores/ListStore'
import { dateToDay } from '../../../shared-lib/utils'
import { groupBy } from '../../../shared-solo/lib/utils'
import { $ExternalClientAddress } from '../../../shared-solo/stores/ExternalClientStore/ExternalClientModel'
import ExternalBookingModel, { $ExternalBooking, $ExternalBookingModel } from './ExternalBookingModel'
import listeners from '../../communication/Listeners'

export interface $EventSuggestion {
  title: string
  notes: string
  address?: $ExternalClientAddress
}

export type CaptureOutcome =
  | { ok: true, paymentId: string }
  | { ok: false, code: string, message: string }

export default class ExternalBookingStore extends ListStore<$ExternalBookingModel> {
  fetchMethodName = 'externalBookings'
  ModelClass = ExternalBookingModel
  @observable accessor suggestedEvents: Array<$EventSuggestion> = []
  externalBookingsSubscription?: { unsubscribe: () => void }

  @action.bound stopListeningOnExternalBookings(): void {
    this.externalBookingsSubscription?.unsubscribe()
    this.externalBookingsSubscription = undefined
  }

  @action.bound clearData(): void {
    this.stopListeningOnExternalBookings()
    super.clearData()
  }

  /**
   * Update the booking list if a change happens on a different account
   */
  @action.bound applyRemoteBookingChange(
    booking: { _id?: string | null, date?: any, allocatedProfessionalId?: string | null },
    previous: { previousDate?: any, previousAllocatedProfessionalId?: string | null },
    userId: string,
  ) {
    const { agendaStore } = this.rootStore

    // Re-allocated away from us: the booking is no longer ours to show, and merging it would put
    // someone else's work in our list. Local removal only — the record itself still exists.
    const lostAllocation = !!previous.previousAllocatedProfessionalId
      && previous.previousAllocatedProfessionalId === userId
      && booking.allocatedProfessionalId !== userId

    if (lostAllocation) {
      if (booking._id) this.removeItem(booking._id, false)
    } else {
      this.mergeData([booking])
    }

    const affectedDays = [booking.date, previous.previousDate]
      .filter(Boolean)
      .map(date => dateToDay(new Date(date)))

    agendaStore.resetCache([...new Set(affectedDays)])
  }

  // Listen on bookings and update bookings on changes
  @action.bound async listenOnExternalBookings_api() {
    const { userStore: { data: { _id } }, communication, dropdownMessageHandler, i18n: { s } } = this.rootStore
    const { applyRemoteBookingChange } = this

    // The subscription is bound to the id of whoever was signed in when it opened, so an account
    // switch must drop it — otherwise the previous account's bookings merge into this store.
    this.stopListeningOnExternalBookings()

    this.externalBookingsSubscription = communication.client.subscribe<Subscription>({
      query: listeners.externalBookingUpdated,
      variables: { userId: _id },
    }).subscribe({
      next(res) {
        const update = res.data?.externalBookingUpdated
        const data = update?.externalBooking

        if (!data) return

        applyRemoteBookingChange(data, {
          previousDate: update?.previousDate,
          previousAllocatedProfessionalId: update?.previousAllocatedProfessionalId,
        }, _id)
      },
      error(err: any) {
        console.error(err)
        dropdownMessageHandler.showDropdownMessage({
          title: s('There was an issue fetching the latest booking data. Please refresh.'),
          type: 'error',
          closeInterval: 1000,
        })
      },
    })

    return this.externalBookingsSubscription
  }

  getEventSuggetsions_api = async () => {
    const { communication: { requester } } = this.rootStore

    this.setLoading(true)

    const externalBookingsRequest = await requester.externalBookings({
      filter: {
        type: 'event',
        createdAt: {
          $gte: moment('2022-03-15').toDate(), // Remove old converted events from suggestions
        },
      },
      sort: {
        updatedAt: -1,
      },
      pagination: {
        limit: 25,
      },
    })

    this.setLoading(false)

    const eventsGroupedByTitle = groupBy(externalBookingsRequest.externalBookings || [], 'title')

    const suggestedEvents = Object.keys(eventsGroupedByTitle).map(e => {
      const event: $ExternalBooking = eventsGroupedByTitle[e][0]

      return {
        title: event.title,
        notes: event.notes,
        address: event.address,
      }
    })

    runInAction(() => {
      this.suggestedEvents = suggestedEvents
    })
  }

  getClientsLastBooking_api = async (externalClientId: string) => {
    const { communication: { requester } } = this.rootStore

    this.setLoading(true)

    const externalBookingsRequest = await requester.externalBookings({
      filter: {
        externalClientId,
      },
      sort: {
        date: -1,
      },
      pagination: {
        limit: 1,
      },
    })

    this.setLoading(false)

    const { externalBookings } = externalBookingsRequest
    if (externalBookings?.[0]) {
      return externalBookings[0].items?.filter(item => item.treatmentId).map(item => item.treatmentId!)
    }

    return []
  }

  checkForConflicts_api = async (booking: $ExternalBooking) => {
    const { communication: { requester } } = this.rootStore

    // Check conflicts against the performer, not the auth user: for salon bookings professionalId is
    // the salon and allocatedProfessionalId is the member, so the member's own calendar is what must
    // be checked. Falling back to professionalId keeps solo (non-salon) bookings unchanged.
    const data = {
      _id: booking._id,
      professionalId: booking.allocatedProfessionalId || booking.professionalId || undefined,
      bufferTime: booking.bufferTime,
      date: booking.date,
      items: booking.items,
    }

    this.setLoading(true)

    const { bookingHasConflicts } = await requester.bookingHasConflicts({ data })

    this.setLoading(false)

    return bookingHasConflicts
  }

  getBookingForDashboard = async (filters: DashboardBookingFilter[]) => {
    const { communication: { requester } } = this.rootStore

    const { soloBookingsForDashboard } = await requester.soloBookingsForDashboard({ filters })

    const allBookings = soloBookingsForDashboard.flatMap(booking => booking.externalBookings)

    this.mergeData(allBookings)

    const data = soloBookingsForDashboard.map(booking => {
      return {
        filter: booking.filter,
        externalBookingIds: booking.externalBookingIds,
      }
    })

    return data
  }

  captureTerminalPayment_api = async (data: {
    paymentId: string
    externalBookingId: string
    tipAmount: number
  }): Promise<CaptureOutcome> => {
    const { communication: { requester } } = this.rootStore

    const { captureTerminalPayment: capture } = await requester.captureTerminalPayment({ data })

    if (capture.error) {
      return { ok: false, code: capture.error.code || '', message: capture.error.message || '' }
    }

    const booking = this.findItemById(data.externalBookingId)

    if (capture.externalBooking && booking) {
      booking.merge(capture.externalBooking)
      this.setEditItem(booking.data)
    }

    return { ok: true, paymentId: capture.id || '' }
  }
}

export type $ExternalBookingStore = ExternalBookingStore
